import { I } from '../data/tokens';
import { SKILLS } from '../data';
import SectionLabel from './SectionLabel';

export function HeroSection({ onViewWork }) {
  return (
    <section id="hero" style={I.section} className="section-pad">
      <div style={I.heroInner} className="fade-up">

        <p style={I.heroEyebrow}>Welcome!</p>

        {/* Name block */}
        <div style={{ marginBottom: '1.25rem' }} className="hero-title">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.15rem' }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 'clamp(0.7rem, 1.2vw, 0.85rem)', color: '#aaa', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 400 }}>
              Hello, I'am
            </span>
          </div>
          <h1 style={{ fontSize: 'clamp(2.8rem, 6vw, 5.2rem)', lineHeight: 1, fontWeight: 400, letterSpacing: '-0.03em', fontFamily: "'DM Serif Display', Georgia, serif", margin: 0 }}>
            <span style={{ display: 'block', color: '#1A1A1A' }}>Ismil Nadaf</span>
            <span style={{ display: 'block', color: '#1A1A1A', position: 'relative' }}>
              <span style={{ position: 'absolute', bottom: '4px', left: 0, width: '100%', height: '2px', background: 'linear-gradient(90deg, #1A1A1A 0%, transparent 100%)', opacity: 0.15, borderRadius: '2px' }} />
            </span>
          </h1>
          <div style={{ marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{ width: '24px', height: '1px', background: '#1A1A1A', opacity: 0.3, display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 'clamp(0.68rem, 1.1vw, 0.8rem)', color: '#888', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 400 }} className="hero-muted">
              Specialist · Strategist · Designer · Leader
            </span>
          </div>
        </div>

        {/* Bio */}
        <p style={I.heroBio} className="hero-bio">
          I firmly believe that technology has the power to revolutionize the way we learn, communicate, and create. My mission is to craft products and services that harmoniously bridge the gap between humans and technology, empowering individuals to reach their full potential.
        </p>

        {/* Stats */}
        <div style={I.heroStats} className="hero-stats">
          {[
            { n: '14',     l: 'Years of Digital Innovation' },
            { n: 'Global', l: 'Experience' },
            { n: '4+',     l: 'Industries Transformed' },
          ].map((s) => (
            <div key={s.n} style={I.heroStat}>
              <span style={I.heroStatNum}>{s.n}</span>
              <span style={I.heroStatLabel}>{s.l}</span>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div style={I.heroActions} className="hero-actions">
          <button style={I.btnPrimary} onClick={onViewWork}>View work</button>
          <a
            href="https://www.linkedin.com/in/ismil-nadaf-a31b7529/"
            target="_blank"
            rel="noreferrer"
            style={I.btnLink}
          >
            LinkedIn ↗
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={I.heroScroll} className="hero-scroll">
        <span style={I.heroScrollLine} className="scroll-line" />
        <span style={I.heroScrollText}>scroll</span>
      </div>
    </section>
  );
}

export function AboutSection() {
  return (
    <section id="about" style={{ ...I.section, minHeight: 'unset', justifyContent: 'flex-start' }} className="section-pad">
      <SectionLabel label="About" />
      <h2 style={I.sectionTitle}>
        Strategic designer at the intersection of human experience and business impact.
      </h2>
      <p style={{ ...I.bodyText, marginTop: '1.25rem' }}>
        Strategic Product Design Leader with over 14 years of experience driving digital transformation across Banking, Fintech, Electrical Power, Marketing, E-commerce, and Software Development. Expert in blending human-centered design with business objectives to deliver high-impact products, including AI-powered platforms and complex design systems.
      </p>
      <p style={{ ...I.bodyText, marginTop: '1.25rem' }}>
        Proven track record of leveraging design to drive revenue growth from $8M in delivered value and $2.3M annual savings.
      </p>
      <div style={I.skillsRow}>
        {SKILLS.map((skill) => (
          <span key={skill} style={I.skillChip}>{skill}</span>
        ))}
      </div>
    </section>
  );
}

export function ProjectsSection({ projects, onOpen }) {
  return (
    <section id="projects" style={I.section} className="section-pad">
      <SectionLabel label="Featured Projects" />
      <h2 style={I.sectionTitle}>Selected work.</h2>
      <p style={I.projectsHint}>Click any project to view the full case study.</p>
      <div style={I.projectsList}>
        {projects.map((project, i) => (
          <ProjectCard key={i} project={project} index={i} onClick={() => onOpen(i)} />
        ))}
      </div>
    </section>
  );
}

import { useState } from 'react';
import FlatIcon from './FlatIcon';

function ProjectCard({ project, index, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        ...I.projectCard,
        borderBottomColor: hovered ? project.accent : 'rgba(0,0,0,0.08)',
        transform:         hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition:        'all 0.3s ease',
        cursor:            'pointer',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={I.projectCardTop} className="project-card-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={I.projectIndex}>0{index + 1}</span>
          <FlatIcon name={project.icon || "rocket"} size={22} color={hovered ? project.accent : '#1A1A1A'} style={{ transition: 'color 0.25s ease' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{
            ...I.projectViewHint,
            opacity:   hovered ? 1 : 0,
            transform: hovered ? 'translateX(0)' : 'translateX(-6px)',
            transition: 'all 0.25s ease',
          }}>
            View case study →
          </span>
        </div>
      </div>
      <div style={{ ...I.projectTitle, color: hovered ? project.accent : '#1A1A1A', transition: 'color 0.25s ease' }}>
        {project.title}
      </div>
      <p style={I.projectDesc}>{project.description}</p>
      <div style={I.projectTags}>
        {project.tags.map((tag) => (
          <span
            key={tag}
            style={{
              ...I.projectTag,
              background: hovered ? `${project.accent}18` : 'transparent',
              border: `1px solid ${hovered ? project.accent + '40' : 'rgba(0,0,0,0.08)'}`,
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

export function CareerSection() {
  const { CAREER } = require('../data');
  const work = CAREER.filter((c) => !c.isEducation);
  const edu  = CAREER.filter((c) =>  c.isEducation);

  return (
    <section id="timeline" style={{ ...I.section, minHeight: 'unset', justifyContent: 'flex-start' }} className="section-pad">
      <SectionLabel label="Career" />
      <h2 style={I.sectionTitle}>Experience &amp; education.</h2>

      <div style={{ ...I.timeline, marginTop: '1.5rem' }}>
        {work.map((item, i) => (
          <TimelineItem key={i} item={item} last={i === work.length - 1} />
        ))}
      </div>

      <div style={{ marginTop: '0px' }}>
        <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.08)', marginBottom: '3rem' }} />
        <SectionLabel label="Education" />
        <div style={I.timeline}>
          {edu.map((item, i) => (
            <TimelineItem key={i} item={item} last={i === edu.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TimelineItem({ item, last }) {
  return (
    <div style={I.timelineItem}>
      <div style={I.timelineLeft}>
        <span style={I.timelineYear}>{item.year}</span>
        {!last && <div style={I.timelineLine} />}
      </div>
      <div style={I.timelineRight}>
        <span style={I.timelineRole}>{item.role}</span>
        <span style={I.timelineCompany}>{item.company}</span>
        <span style={I.timelineNote}>{item.note}</span>
      </div>
    </div>
  );
}

export function FooterSection() {
  return (
    <section id="footer" style={{ ...I.footerSection }} className="footer-section">
      <div style={{ ...I.footerInner, flex: 1, justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
        <p style={I.footerEyebrow}>Ready to transform your digital experience?</p>
        <h2 style={I.footerTitle} className="footer-title">Let's Connect.</h2>
        <p style={I.footerSubText}>
          With 14 years of expertise in Product &amp; UX design and digital transformation, I bring strategic vision and proven results to every project.
        </p>
        <div style={I.footerLinks}>
          <a href="#" style={{ ...I.footerLink, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.03em', textTransform: 'none', fontSize: '0.82rem', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '2px' }}>Get in touch →</a>
          <a href="https://linkedin.com/in/ismilnadaf" target="_blank" rel="noreferrer" style={I.footerLink}>LinkedIn</a>
        </div>
      </div>
      <div style={I.footerBar} className="footer-bar">
        <span style={I.footerBarText}>© 2025 Ismil Nadaf</span>
        <span style={I.footerBarText}>Crafted with passion for human-centered design.</span>
      </div>
    </section>
  );
}
